import { useEffect, useState } from "react";
import aboutImage1 from "../image/about1.webp";
import aboutImage2 from "../image/about2.webp";
import aboutImage3 from "../image/about3.webp";

const slides = [aboutImage3, aboutImage2, aboutImage1];

const missionItems = [
  {
    title: "Customer-Centric Excellence",
    description: "surpassing expectations, ensuring success.",
  },
  {
    title: "Integrity & Ethics",
    description: "building trust through honesty and confidentiality.",
  },
  {
    title: "Continuous Improvement",
    description: "embracing learning and raising quality standards.",
  },
  {
    title: "Quality Services",
    description: "delivering services that meet customer expectations.",
  },
  {
    title: "Innovation & Adaptability",
    description: "leveraging technology to meet evolving needs.",
  },
];

const coreValues = [
  {
    title: "Technical Expertise",
    description: "Top-quality performance through skilled professionals.",
  },
  {
    title: "Commitment & Excellence",
    description: "Consistently achieving exceptional performance and quality.",
  },
  {
    title: "Sustainability",
    description: "Embracing learning and raising quality standards.",
  },
  {
    title: "Integrity",
    description: "Upholding honesty, transparency, and ethical conduct.",
  },
  {
    title: "Continuous Improvement",
    description: "Fostering ongoing learning and innovation.",
  },
  {
    title: "Responsible Stewardship",
    description: "Managing resources wisely, minimizing ecological impact.",
  },
  {
    title: "Social Responsibility",
    description: "Contributing positively to society and communities.",
  },
];

const Container = ({ children, className = "" }) => (
  <div
    className={`mx-auto max-w-[1360px] px-4 sm:px-6 lg:px-10 ${className}`}
  >
    {children}
  </div>
);

const AboutHeading = ({ title }) => (
  <>
    <p className="text-sm font-semibold text-[#d4a017]">About</p>

    <h2 className="mt-1 text-4xl font-bold leading-tight text-[#122a72] sm:text-5xl lg:text-[35px]">
      {title}
    </h2>
  </>
);

export default function AboutUs() {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 3500);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="min-h-full bg-[#f5f5f5] text-[#0f172a]">
      {/* Hero */}
      <Container className="grid items-center gap-8 py-6 lg:grid-cols-[1fr_0.95fr] lg:gap-10 lg:py-8">
        <div className="lg:max-w-[92%]">
          <AboutHeading title="DYC Global" />

          <div className="mt-5 text-sm font-medium leading-7 text-[#1f2937] sm:text-[15px] sm:leading-8">
            <p>
              DYC One is the flagship Digital Assurance and Enterprise
              Operations Platform developed by DYC Global Private Limited.
              Built to meet the rigorous demands of inspection, quality
              assurance, and technical advisory work, it brings inspection
              management, workforce administration, compliance, and business
              operations together in a single, secure, and intelligent system.
              Designed for inspectors, coordinators, auditors, managers, and
              leadership, DYC One delivers real-time visibility,
              data-driven decision-making, and process standardization across
              every project.
            </p>
          </div>
        </div>

        <div className="relative h-[250px] overflow-hidden rounded-[18px] bg-white shadow-[0_10px_30px_rgba(15,23,42,0.12)] sm:h-[360px] lg:h-[300px]">
          {slides.map((image, index) => (
            <div
              key={image}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                index === currentSlide ? "opacity-100" : "opacity-0"
              }`}
            >
              <img
                src={image}
                alt={`DYC Global slide ${index + 1}`}
                className="h-full w-full object-cover"
              />
            </div>
          ))}
        </div>
      </Container>

      {/* Company Overview */}
      <Container className="py-6 sm:py-8">
        <div className="lg:max-w-[92%]">
          <AboutHeading title="DYC Global Private Limited" />

          <div className="mt-5 text-center text-sm font-medium leading-7 text-[#1f2937] sm:text-[15px] sm:leading-8">
            <p>
              DYC Global Private Limited is a fast-growing organization
              specializing in technical inspection, verification, and
              conformity assessment across diverse industrial sectors.
              Operating as a Type 'A' independent third-party inspection body,
              DYC Global delivers engineering and quality services aligned to
              national and international codes, standards, and client-specific
              requirements — enhancing product integrity, safety, and
              operational efficiency while minimizing risk for asset owners.
            </p>
          </div>
        </div>
      </Container>

      {/* Vision & Mission */}
      <Container className="py-8">
        <div className="grid gap-8 lg:grid-cols-2">
          <div>
            <h2 className="text-[30px] font-bold text-[#122a72]">
              Our Vision
            </h2>

            <p className="mt-5 text-[15px] leading-6">
              At DYC Global, our vision is centred on Empowering Excellence,
              Connectivity, and Responsiveness — driving our dedication to
              delivering exceptional services and fostering lasting
              connections.
            </p>
          </div>

          <div>
            <h2 className="text-[30px] font-bold text-[#122a72]">
              Our Mission
            </h2>

            <ul className="mt-5 list-disc pl-5 text-[15px] leading-7 space-y-2">
              {missionItems.map((item) => (
                <li key={item.title}>
                  <strong>{item.title}</strong> — {item.description}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Container>

      {/* Core Values */}
      <Container className="py-10">
        <h2 className="mb-8 text-center text-[30px] font-bold text-[#122a72]">
          Our Core Values
        </h2>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {coreValues.map((item) => (
            <div
              key={item.title}
              className="rounded-xl bg-white p-6 text-center shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
            >
              <h3 className="mb-3 font-bold text-[#122a72]">
                {item.title}
              </h3>

              <p className="text-sm leading-6 text-gray-600">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </Container>

      {/* Slogan */}
      <Container className="py-8">
        <div className="rounded-2xl bg-[#122a72] py-12 text-center shadow-lg">
          <p className="text-sm font-semibold uppercase tracking-[0.25em] text-white/80">
            Our Slogan
          </p>

          <h2 className="mt-3 text-2xl font-bold text-[#d4a017] md:text-3xl">
            Ensuring Sustainable Excellence
          </h2>
        </div>
      </Container>
    </section>
  );
}